import { Button, Tooltip } from "antd";
import { useOne, useTranslate } from "@refinedev/core";
import { CSSProperties, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StoryboardApprovalModal } from "./StoryboardApprovalModal";
import { AnalyticsEvents, StoryboardMediaGenerateEvent } from "types/analytics";
import { useAnalytics } from "hooks/useAnalytics";
import { TrackedButton } from "components/TrackedButton";
import type { Media, MediaGenerationMode } from "../types";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useRegenerateOrApprove } from "../projects/hooks/useRegenerateOrApprove";
import { isStoryboardAwaitingApproval } from "../projects/helpers/helper";

const MEDIA_GENERATING_POLLING_INTERVAL = 30000; // 30 seconds

export const GenerateMediaButton = ({
  projectId,
  storyboardId,
  storyboardSettingsId,
  storyboardStatus,
  mediaId,
  mediaGenerationMode,
  style,
  text,
  onBeforeGenerate,
  confirmBeforeGenerate,
  isStoryboardSaving = false,
  estimatedGenerationTime,
  trackingPage,
  isPublished = false,
  onSuccess,
}: {
  projectId: string;
  storyboardId?: string;
  /** Storyboard settings id (e.g. "default") — used for the approve endpoint path. */
  storyboardSettingsId?: string;
  /** Drives approve-vs-regenerate: "Reviewing" needs approval even if media already exists. */
  storyboardStatus?: string;
  mediaId?: string;
  mediaGenerationMode?: MediaGenerationMode;
  style?: CSSProperties;
  text?: string;
  onBeforeGenerate?: () => Promise<void>;
  /** Optional gate run on click; resolve false to abort generation (e.g. a confirm dialog). */
  confirmBeforeGenerate?: () => Promise<boolean>;
  isStoryboardSaving?: boolean;
  estimatedGenerationTime?: string;
  trackingPage: StoryboardMediaGenerateEvent["page"];
  isPublished?: boolean;
  /**
   * Called on a successful generate/regenerate. Defaults to navigating to the
   * project's detail page. Receives `fromStoryboardApproval` when the success
   * came from approving the storyboard (not a plain regenerate) — detail.tsx
   * reads that flag from navigation state to avoid bouncing back to the
   * review step while the backend's status is still catching up.
   */
  onSuccess?: (approvalState?: { fromStoryboardApproval: true }) => void;
}) => {
  const t = useTranslate();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();

  const handleGenerationSuccess = (approvalState?: {
    fromStoryboardApproval: true;
  }) => {
    if (onSuccess) {
      onSuccess(approvalState);
    } else {
      navigate(`/media/projects/${projectId}`, { state: approvalState });
    }
  };

  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isBeforeGenerateLoading, setIsBeforeGenerateLoading] = useState(false);

  // poll media to make sure we use the latest status
  const { data: mediaData } = useOne<Media>({
    resource: `media/media`,
    id: mediaId,
    queryOptions: {
      refetchInterval(data) {
        return data?.data.status === "Generating"
          ? MEDIA_GENERATING_POLLING_INTERVAL
          : false;
      },
      enabled: !!mediaId,
    },
  });

  // Storyboard waiting for review needs approval even if a (now stale) media already exists.
  const needsApproval = !mediaId || isStoryboardAwaitingApproval(storyboardStatus);

  const { regenerateOrApprove, isProcessing } = useRegenerateOrApprove({
    projectId,
    storyboardSettingsId,
    storyboardStatus,
    mediaId,
    mediaStatus: mediaData?.data.status,
  });

  const isMediaGenerationLoading = isProcessing || isBeforeGenerateLoading;

  const handleBeforeGenerate = async () => {
    if (onBeforeGenerate) {
      setIsBeforeGenerateLoading(true);
      try {
        await onBeforeGenerate();
      } finally {
        setIsBeforeGenerateLoading(false);
      }
    }
    if (storyboardId) {
      trackEvent(AnalyticsEvents.StoryboardMediaGenerate, {
        project_id: projectId,
        storyboard_id: storyboardId,
        page: trackingPage,
      });
    }
  };

  const handleApprove = async () => {
    try {
      await handleBeforeGenerate();
      // regenerateOrApprove swallows its own errors and resolves false on
      // failure instead of throwing — only navigate on a real success.
      const success = await regenerateOrApprove();
      if (success) {
        handleGenerationSuccess({ fromStoryboardApproval: true });
      }
    } catch (e) {
      console.debug(e);
    }
  };

  const handleRegenerate = async () => {
    await handleBeforeGenerate();

    try {
      // Same as above: only refresh the view on a reported success.
      const success = await regenerateOrApprove();
      if (success && projectId) handleGenerationSuccess();
    } catch (e) {
      console.error(e);
    }
  };

  const onGenerateButtonClick = async () => {
    if (confirmBeforeGenerate && !(await confirmBeforeGenerate())) return;
    if (needsApproval) {
      setIsApprovalModalOpen(true);
    } else {
      handleRegenerate().catch((error) => {
        console.error("Error during regeneration:", error);
      });
    }
  };

  // if no media created yet, show the button anyway
  // if not, wait for fresh status
  if (mediaId && !mediaData?.data.status) return null;
  if (mediaGenerationMode !== "Video" && mediaGenerationMode !== "Audio")
    return null;

  // A storyboard back in review still needs approval even if its (stale)
  // media is mid-generation — don't let that generation lock the button.
  if (mediaData?.data.status === "Generating" && !needsApproval) {
    return (
      <Button
        type="primary"
        shape="round"
        loading
        // don't show disabled,not visible enough
        style={{ pointerEvents: "none" }}
      >
        {t(
          mediaGenerationMode === "Audio"
            ? "media.projects.detail.generatingAudio"
            : "media.projects.detail.generatingVideo"
        )}
      </Button>
    );
  }

  const generateButton = (
    <TrackedButton
      type="primary"
      shape="round"
      icon={<ArrowRightOutlined />}
      iconPosition="end"
      loading={isMediaGenerationLoading}
      disabled={isMediaGenerationLoading || isPublished}
      onClick={() => void onGenerateButtonClick()}
      style={style}
    >
      {text ?? t("components.templates.index.generateMedia")}
    </TrackedButton>
  );

  return (
    <>
      {isPublished ? (
        <Tooltip
          title={t(
            "media.projects.detail.generateVideoDisabledPublishedTooltip"
          )}
        >
          <span style={{ display: "inline-block" }}>{generateButton}</span>
        </Tooltip>
      ) : (
        generateButton
      )}
      <StoryboardApprovalModal
        isOpen={isApprovalModalOpen}
        handleOk={async () => {
          try {
            await handleApprove();
            setIsApprovalModalOpen(false);
          } catch (error) {
            console.error("Error during save and approval:", error);
          }
        }}
        handleCancel={() => setIsApprovalModalOpen(false)}
        saving={isProcessing || isStoryboardSaving}
        estimatedGenerationTime={estimatedGenerationTime}
      />
    </>
  );
};
